import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Printer, ArrowLeft, CheckCheck, Send, X as XIcon, Trash2, FileText } from "lucide-react";
import SlottedDiagram from "@/components/SlottedDiagram";
import ChargesEditor from "@/components/ChargesEditor";

export default function SlottedDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const canEditCharges = user?.role === "admin" || user?.role === "employee";
  const [q, setQ] = useState(null);
  const [bomOpen, setBomOpen] = useState(false);
  const [bomRows, setBomRows] = useState([]);
  const [bomBusy, setBomBusy] = useState(false);

  const canUseBom = user?.role === "admin" || user?.role === "employee";

  useEffect(() => {
    api.get(`/slotted/quotations/${id}`).then((r) => setQ(r.data));
  }, [id]);

  const openBom = async () => {
    setBomOpen(true); setBomBusy(true);
    try {
      const { data } = await api.get(`/slotted/quotations/${id}/bom`);
      setBomRows(data);
    } finally { setBomBusy(false); }
  };

  const changeStatus = async (status) => {
    const { data } = await api.patch(`/slotted/quotations/${id}/status`, { status });
    setQ(data);
    toast.success(`Marked ${status}`);
  };
  const del = async () => {
    if (!window.confirm("Delete this slotted-angle quotation?")) return;
    await api.delete(`/slotted/quotations/${id}`);
    navigate("/slotted-quotations");
  };

  if (!q)
    return (
      <Layout>
        <div className="p-12 label-tech">Loading…</div>
      </Layout>
    );

  return (
    <Layout>
      <div className="no-print">
        <PageHeader
          eyebrow={`Slotted · ${q.status}`}
          title={q.quote_number}
          subtitle={`Created ${new Date(q.created_at).toLocaleString("en-IN")} by ${q.created_by_name}`}
          actions={
            <div className="flex items-center gap-2">
              <button onClick={() => navigate(-1)} className="btn-ghost">
                <ArrowLeft size={16} /> Back
              </button>
              {canUseBom && (
                <button onClick={openBom} className="btn-secondary" data-testid="sa-detail-bom-btn">
                  <FileText size={16} /> BOM
                </button>
              )}
              <button onClick={() => window.print()} className="btn-primary" data-testid="sa-print-btn">
                <Printer size={16} /> Print / PDF
              </button>
            </div>
          }
        />
        <div className="px-8 pt-6 flex flex-wrap items-center gap-2">
          <span className="label-tech mr-2">Actions</span>
          <button onClick={() => changeStatus("sent")} className="btn-secondary">
            <Send size={14} /> Mark Sent
          </button>
          <button onClick={() => changeStatus("approved")} className="btn-secondary">
            <CheckCheck size={14} /> Approved
          </button>
          <button onClick={() => changeStatus("rejected")} className="btn-secondary">
            <XIcon size={14} /> Rejected
          </button>
          {user.role === "admin" && (
            <button onClick={del} className="btn-danger ml-auto">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="p-8">
        <div className="panel-heavy max-w-5xl mx-auto" data-testid="sa-pdf">
          <div className="p-8 border-b-2 border-ink flex items-start justify-between">
            <div className="flex items-start gap-5">
              <img src="/brand/adarsh-logo.png" alt="Adarsh Udyog" className="h-20 w-auto object-contain" />
              <div>
                <div className="label-tech mt-1">A Complete Storage Solution</div>
                <div className="text-xs text-ink-muted mt-3 mono">
                  Plot No. 2, Nangli Sakrawati Industrial Area <br />
                  adarshudyog@gmail.com · +91 98100 98860
                  {q.company_gstin ? (
                    <>
                      <br />GSTIN: {q.company_gstin}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="label-tech">Slotted Angle Quotation</div>
              <div className="mono font-bold text-2xl mt-1">{q.quote_number}</div>
              <div className="label-tech mt-4">Date</div>
              <div className="mono text-sm mt-1">{new Date(q.created_at).toLocaleDateString("en-IN")}</div>
              <div className="label-tech mt-4">Status</div>
              <div className="mono font-bold uppercase">{q.status}</div>
            </div>
          </div>

          <div className="p-8 grid grid-cols-2 gap-8 border-b border-line">
            <div>
              <div className="label-tech mb-3">Billed to</div>
              <div className="font-bold text-ink">{q.client?.company || "—"}</div>
              <div className="text-sm text-ink-muted">{q.client?.name}</div>
              <div className="text-sm text-ink-muted">{q.client?.contact}</div>
              <div className="text-sm text-ink-muted">{q.client?.address}</div>
              {q.client?.gstin && <div className="mono text-xs mt-2">GSTIN: {q.client.gstin}</div>}
            </div>
            <div>
              <div className="label-tech mb-3">Summary</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-ink-muted">Line items</span>
                <span className="mono font-bold text-right">{(q.items || []).length || 1}</span>
                <span className="text-ink-muted">Total weight</span>
                <span className="mono font-bold text-right">{fmtNum(q.total_weight_kg, 2)} kg</span>
                <span className="text-ink-muted">Rate</span>
                <span className="mono font-bold text-right">₹{parseFloat(Number(q.result.price_per_kg).toFixed(2))} / kg</span>
              </div>
            </div>
          </div>

          {(q.items && q.items.length > 0) ? (
            /* MULTI-ITEM: one spec + BOM block per line */
            q.items.map((it, idx) => (
              <div key={idx} className="p-8 border-b border-line">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-tech">Line #{idx + 1} · qty {it.quantity}{it.label ? ` · ${it.label}` : ""}</div>
                  <div className="mono text-sm font-bold">
                    {fmtNum(it.result.total_weight_kg, 2)} kg × {it.quantity} = {fmtNum(it.result.total_weight_kg * it.quantity, 2)} kg · {fmtINR(it.result.total_price * it.quantity)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-4 bg-surfaceAlt p-3">
                  <span className="text-ink-muted">Size (L × D × H)</span>
                  <span className="mono font-bold text-right">{it.config.length_in}" × {it.config.depth_in}" × {it.config.height_in}"</span>
                  <span className="text-ink-muted">Section / angle</span>
                  <span className="mono font-bold text-right">{it.config.section} · {it.config.angle_thickness_mm}mm</span>
                  <span className="text-ink-muted">Shelves</span>
                  <span className="mono font-bold text-right">{it.config.shelves} × {it.config.shelf_thickness_mm}mm</span>
                  {it.config.partitions > 0 && (<>
                    <span className="text-ink-muted">Partitions</span>
                    <span className="mono font-bold text-right">{it.config.partitions} × {it.config.partition_width_in}"×{it.config.partition_height_in}" @ {it.config.partition_thickness_mm}mm</span>
                  </>)}
                  {(it.config.back_sheets > 0 || it.config.side_sheets > 0) && (<>
                    <span className="text-ink-muted">Back / Side</span>
                    <span className="mono font-bold text-right">{it.config.back_sheets || 0} back · {it.config.side_sheets || 0} side @ {it.config.cover_thickness_mm}mm</span>
                  </>)}
                </div>
                <table className="w-full grid-lines">
                  <thead>
                    <tr>
                      <th>Component</th>
                      {!isDealer && <th>Section / Size</th>}
                      {!isDealer && <th className="num">T (mm)</th>}
                      <th className="num">Approx unit wt</th>
                      <th className="num">Qty / rack</th>
                      <th className="num">Total wt (× {it.quantity})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {it.result.components.map((c) => (
                      <tr key={c.component}>
                        <td className="font-bold uppercase tracking-wider text-xs">{c.component}</td>
                        {!isDealer && <td className="mono text-xs">{c.section}</td>}
                        {!isDealer && <td className="num mono text-xs">{c.thickness_mm > 0 ? c.thickness_mm : "—"}</td>}
                        <td className="num">{fmtNum(c.unit_weight_kg, 3)} kg</td>
                        <td className="num">{c.quantity}</td>
                        <td className="num font-bold">{fmtNum(c.total_weight_kg * it.quantity, 2)} kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-5">
                  <SlottedDiagram cfg={it.config} />
                </div>
              </div>
            ))
          ) : (
            /* LEGACY SINGLE-ITEM */
            <>
              <div className="p-8 border-b border-line">
                <div className="label-tech mb-3">Specification</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <span className="text-ink-muted">Size (L × D × H)</span>
                  <span className="mono font-bold text-right">
                    {q.config.length_in}" × {q.config.depth_in}" × {q.config.height_in}"
                  </span>
                  <span className="text-ink-muted">Shelves</span>
                  <span className="mono font-bold text-right">{q.config.shelves}</span>
                  <span className="text-ink-muted">Section</span>
                  <span className="mono font-bold text-right">{q.config.section}</span>
                  <span className="text-ink-muted">Angle thickness</span>
                  <span className="mono font-bold text-right">{q.config.angle_thickness_mm} mm</span>
                  <span className="text-ink-muted">Shelf thickness</span>
                  <span className="mono font-bold text-right">{q.config.shelf_thickness_mm} mm</span>
                </div>
              </div>
              <div className="p-8">
                <div className="label-tech mb-3">Bill of materials</div>
                <table className="w-full grid-lines">
                  <thead>
                    <tr>
                      <th>Component</th>
                      {!isDealer && <th>Section / Size</th>}
                      {!isDealer && <th className="num">T (mm)</th>}
                      <th className="num">Approx unit wt</th>
                      <th className="num">Qty</th>
                      <th className="num">Total wt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.result.components.map((c) => (
                      <tr key={c.component}>
                        <td className="font-bold uppercase tracking-wider text-xs">{c.component}</td>
                        {!isDealer && <td className="mono text-xs">{c.section}</td>}
                        {!isDealer && <td className="num mono text-xs">{c.thickness_mm > 0 ? c.thickness_mm : "—"}</td>}
                        <td className="num">{fmtNum(c.unit_weight_kg, 3)} kg</td>
                        <td className="num">{c.quantity}</td>
                        <td className="num font-bold">{fmtNum(c.total_weight_kg, 2)} kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-5">
                  <SlottedDiagram cfg={q.config} />
                </div>
              </div>
            </>
          )}

          <div className="p-8 border-t-2 border-ink">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <div className="label-tech">Total Weight</div>
                <div className="mono text-2xl font-bold mt-1">{fmtNum(q.total_weight_kg, 2)} kg</div>
              </div>
              <div>
                <div className="label-tech">Rate</div>
                <div className="mono text-2xl font-bold mt-1">
                  ₹{parseFloat(Number(q.result.price_per_kg).toFixed(2))} / kg
                </div>
              </div>
              <div className="sm:text-right space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="label-tech">Subtotal</span>
                  <span className="mono font-bold">{fmtINR(q.total_price)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="label-tech">GST @ {fmtNum(q.gst_percent || 0, 0)}%</span>
                  <span className="mono font-bold">{fmtINR(q.gst_amount || 0)}</span>
                </div>
                {(q.freight_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="label-tech">Freight</span>
                    <span className="mono font-bold">{fmtINR(q.freight_amount)}</span>
                  </div>
                )}
                {(q.installation_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="label-tech">Installation</span>
                    <span className="mono font-bold">{fmtINR(q.installation_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t-2 border-ink">
                  <span className="label-tech">Grand Total</span>
                  <span className="display font-black text-3xl text-brand">
                    {fmtINR(q.grand_total_price || q.total_price)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {canEditCharges && (
            <ChargesEditor
              endpoint={`/slotted/quotations/${id}/charges`}
              freight={q.freight_amount}
              installation={q.installation_amount}
              onUpdated={setQ}
            />
          )}

          {q.notes && (
            <div className="p-8 pt-0 border-t border-line">
              <div className="label-tech mb-2">Notes</div>
              <div className="text-sm text-ink whitespace-pre-wrap">{q.notes}</div>
            </div>
          )}
          <div className="p-8 pt-4 border-t border-line text-xs text-ink-muted">
            This quotation is valid for 7 days from the date of issue and is subject to final approval. Taxes as
            applicable.
          </div>
        </div>
      </div>

      {bomOpen && (
        <DetailBomModal rows={bomRows} busy={bomBusy} quote={q} onClose={() => setBomOpen(false)} />
      )}
    </Layout>
  );
}

function DetailBomModal({ rows, busy, quote, onClose }) {
  const groups = Array.isArray(rows) && rows.length && rows[0] && Array.isArray(rows[0].lines)
    ? rows
    : (rows && rows.length
        ? [{ item_index: 1, label: "", summary: "", quantity: 1, total_pieces: rows.reduce((s,r)=>s+(r.quantity||0),0), lines: rows }]
        : []);
  const total = groups.reduce((s, g) => s + (g.total_pieces || 0), 0);
  useEffect(() => {
    document.body.classList.add("printing-bom");
    return () => document.body.classList.remove("printing-bom");
  }, []);
  const node = (
    <div className="pr-bom-overlay fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="sa-bom-modal">
      <div className="pr-bom-panel bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-line shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-surfaceAlt no-print">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-brand" />
            <div className="mono font-bold uppercase tracking-wider text-sm">Bill of Materials — {quote.quote_number}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn-primary"><Printer size={14} /> Print / PDF</button>
            <button onClick={onClose} className="btn-ghost"><XIcon size={14} /></button>
          </div>
        </div>
        <div className="pr-bom-body flex-1 overflow-y-auto p-6">
          {/* Print-only header */}
          <div className="print-only pb-3 border-b-2 border-black mb-4">
            <div className="flex items-center justify-between">
              <img src="/brand/adarsh-logo.png" alt="Adarsh Udyog" style={{ height: 44, objectFit: "contain" }} />
              <div className="text-center">
                <div className="font-black uppercase tracking-widest text-lg">SLOTTED ANGLE · BOM</div>
                <div className="text-[10px] mono">
                  {quote.quote_number} · {quote.client?.company || quote.client?.name || "—"}
                  {quote.client?.address ? ` · ${quote.client.address}` : ""}
                </div>
              </div>
              <div className="text-[10px] text-right mono">
                <div>Date: {new Date().toLocaleDateString("en-IN")}</div>
                <div>All dims in MM</div>
              </div>
            </div>
          </div>

          <div className="text-xs mb-3 text-ink-muted">
            Cutting list for <b>{quote.quote_number}</b> · grouped by line item · <b>{groups.length}</b> line{groups.length === 1 ? "" : "s"} · <b className="text-ink">{total}</b> pieces
          </div>
          {busy ? (
            <div className="p-10 text-center label-tech">Building BOM…</div>
          ) : groups.length === 0 ? (
            <div className="p-10 text-center label-tech italic text-ink-muted">No BOM lines.</div>
          ) : (
            groups.map((g) => (
              <div key={g.item_index} className="mb-6 bom-group">
                <div className="flex items-baseline justify-between pb-1.5 mb-2 border-b border-ink">
                  <div className="mono font-bold uppercase tracking-wider text-sm text-ink">
                    Item {g.item_index}
                    {g.label ? <span className="ml-2 text-ink-muted normal-case tracking-normal">· {g.label}</span> : null}
                  </div>
                  <div className="text-[0.7rem] mono text-ink-muted">{g.total_pieces} pieces</div>
                </div>
                <div className="text-[0.75rem] mono text-ink-muted mb-2">{g.summary}</div>
                <div className="panel-heavy">
                  <table className="w-full grid-lines">
                    <thead className="bg-black text-white">
                      <tr>
                        <th className="text-left">Component</th>
                        <th className="text-left">Section / Ref</th>
                        <th className="num">T (mm)</th>
                        <th className="num">Width (mm)</th>
                        <th className="num">Length (mm)</th>
                        <th className="num">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.lines.map((r, i) => (
                        <tr key={i}>
                          <td className="font-bold uppercase tracking-wider text-xs">{r.component}</td>
                          <td className="mono text-xs">{r.section}</td>
                          <td className="num mono text-xs">{r.thickness_mm > 0 ? r.thickness_mm : "—"}</td>
                          <td className="num mono">{r.width_mm > 0 ? r.width_mm : "—"}</td>
                          <td className="num mono">{r.length_mm > 0 ? r.length_mm : "—"}</td>
                          <td className="num font-bold">{r.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
